import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { UserAccountEmailInput, type UserAccountInfo } from '@lub0v/santa-utils';
import { Bar, Button, EmailInput, Hint, OtpChallenge, Stack, Typography, showToast } from '@src/components';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import { verifyOtpForSettings } from '@src/pages/account/verifyOtp';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';
import { translateFieldError, validateFieldError } from '@src/validation';
import './EmailField.css';

export interface EmailFieldProps {
  account: UserAccountInfo;
  guest?: boolean;
  challengeToken: string;
  onAccountChange: (account: UserAccountInfo, challengeToken: string) => void;
}

export function EmailField({ account, challengeToken, onAccountChange, guest = false }: EmailFieldProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState(account.email || (guest ? account.verifyEmail : '') || '');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const currentEmail = (account.email || '').toLowerCase().trim();
  const pendingEmail = (account.verifyEmail || '').toLowerCase().trim();
  const showOtp = Boolean(challengeToken && pendingEmail && pendingEmail !== currentEmail);
  const canSave = Boolean(draft.trim() && draft.trim().toLowerCase() !== currentEmail);

  async function save(nextEmail: string) {
    const email = nextEmail.trim();
    if (!email || email.toLowerCase() === currentEmail || busy) {
      return;
    }
    const fieldError = validateFieldError(new UserAccountEmailInput(), 'email', { email });
    if (fieldError) {
      setError(translateFieldError(fieldError, t));
      return;
    }
    setBusy(true);
    const res = await santaClient.accountUpdateEmail({ email }).catch(() => null);
    setBusy(false);
    if (!res) {
      setError(t('switcher.actionError'));
      return;
    }
    if (res.ok && res.data?.account) {
      const next = res.data.account;
      const pending =
        next.verifyEmail && next.verifyEmail.toLowerCase().trim() !== (next.email || '').toLowerCase().trim();
      onAccountChange(next, pending ? res.data.otpChallenge?.challengeToken || '' : '');
      if (!guest || !pending) await dispatch(loadSession());
      setError(undefined);
      if (!pending) {
        showToast(t('account.emailSaved'), 'success');
      }
      return;
    }
    if (guest && res.error?.message === 'EMAIL_ALREADY_USED') {
      setError(t('account.guestEmailAlreadyUsed'));
      return;
    }
    const rateLimited =
      res.error?.status === 429 || res.error?.message === 'Too many requests';
    setError(rateLimited ? t('account.emailRateLimited') : t(resolveApiErrorKey(res.error?.message || '')));
  }

  const saveButton = (
    <Button
      variant="secondary"
      size={guest ? 'medium' : 'small'}
      disabled={!canSave}
      loading={busy}
      onClick={() => { void save(draft); }}
      testId="account-email-save"
    >
      {t(guest ? 'account.guestLinkEmail' : 'account.emailUpdate')}
    </Button>
  );

  const emailInput = (
      <EmailInput
        value={draft}
        onValueChange={(value) => {
          setError(undefined);
          setDraft(value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void save(draft);
          }
        }}
        name="email"
        label={t('account.emailLabel')}
        autoComplete="email"
        error={guest ? undefined : error}
        aria-invalid={guest ? Boolean(error) : undefined}
        action={guest ? undefined : saveButton}
        testId="account-email"
      />
  );

  return (
    <Stack gap={4} wide>
      {guest ? (
        <Bar align="end" stack="narrow" wrap={false} className="guest-email-field__row">
          {emailInput}
          {saveButton}
        </Bar>
      ) : emailInput}
      {guest && error ? <Hint testId="guest-email-error">{error}</Hint> : null}
      {showOtp ? (
        <OtpChallenge
          challengeToken={challengeToken}
          hint={
            <Trans
              i18nKey="account.emailOtpHint"
              values={{ email: account.verifyEmail }}
              components={{ email: <Typography as="span" role="body/strong" /> }}
            />
          }
          onVerify={async (token, code) => {
            const failure = await verifyOtpForSettings(token, code);
            if (failure) {
              return failure;
            }
            const next = await santaClient.account();
            if (next.ok && next.data) {
              onAccountChange(next.data, '');
              showToast(t('account.emailSaved'), 'success');
              await dispatch(loadSession());
            }
            return null;
          }}
          testId="account-email-otp"
        />
      ) : null}
    </Stack>
  );
}
